import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { Crown, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useGetCurrentUser } from "@workspace/api-client-react";
import { useTranslation } from "react-i18next";
import { trackPaywallViewed } from "@/lib/analytics";
import { proFeatureItems } from "@/components/pro-features";

export function Paywall() {
  const [, setLocation] = useLocation();
  const { t } = useTranslation();
  const { data: user, isFetched } = useGetCurrentUser();
  const purchaseHeld = Boolean(
    user?.accessPolicy?.temporaryFree ||
    user?.accessPolicy?.proPurchaseAvailable === false,
  );
  const paywallViewed = useRef(false);
  useEffect(() => {
    if (!isFetched || purchaseHeld || paywallViewed.current) return;
    paywallViewed.current = true;
    trackPaywallViewed();
  }, [isFetched, purchaseHeld]);

  if (purchaseHeld) {
    return (
      <div
        className="mx-auto max-w-md space-y-4 px-4 py-12 text-center"
        data-testid="temporary-free-access"
      >
        <h2 className="text-2xl font-bold">
          {t(
            user?.accessPolicy?.temporaryFree
              ? "accessPolicy.title"
              : "billing.title",
          )}
        </h2>
        <p className="text-muted-foreground">
          {t(
            user?.accessPolicy?.temporaryFree
              ? "accessPolicy.body"
              : "billing.subtitle",
          )}
        </p>
        <Button variant="outline" onClick={() => setLocation("/billing")}>
          {t("billing.title")}
        </Button>
      </div>
    );
  }

  const features = proFeatureItems(t);

  return (
    <div
      className="flex items-center justify-center min-h-[60vh] px-4"
      data-testid="paywall"
    >
      <div className="max-w-md w-full text-center space-y-6">
        <div className="mx-auto w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center">
          <Lock className="w-8 h-8 text-primary" />
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold">Upgrade to Pro</h2>
          <p className="text-muted-foreground text-sm max-w-sm mx-auto">
            This feature requires a Plate OS Pro subscription. Unlock the full
            power of AI-driven nutrition intelligence.
          </p>
        </div>

        <div
          className="rounded-xl border border-border bg-card p-4 text-left space-y-2"
          data-testid="pro-feature-list"
        >
          {features.map((feature) => (
            <div
              key={feature.key}
              className="flex items-center gap-3"
              data-feature={feature.key}
            >
              <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                <feature.icon className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="text-sm text-muted-foreground">
                {feature.label}
              </span>
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            className="w-full gap-2"
            onClick={() => setLocation("/subscription")}
            data-testid="paywall-upgrade-btn"
          >
            <Crown className="w-4 h-4" />
            View Plans & Subscribe
          </Button>
          <p className="text-xs text-muted-foreground">
            Starting at $25.00/month billed annually
          </p>
        </div>
      </div>
    </div>
  );
}
